import { ALL_NOTES, MODE_NAMES_BY_SCALE, MODES, getNotesInMode as getNotesInModeFromMusicTheory, getModeIntervals, getChordNotes, resolveAllNotesIndex } from './musicTheory';
import { getSetting, setSetting } from '../data/userStore';

// Standard tuning defaults for different guitar configurations
export const DEFAULT_4_STRING_TUNING = ['E', 'A', 'D', 'G']; // Standard bass
export const DEFAULT_5_STRING_TUNING = ['B', 'E', 'A', 'D', 'G']; // 5-string bass
export const DEFAULT_6_STRING_TUNING = ['E', 'A', 'D', 'G', 'B', 'E'];
export const DEFAULT_7_STRING_TUNING = ['B', 'E', 'A', 'D', 'G', 'B', 'E'];
export const DEFAULT_8_STRING_TUNING = ['F#', 'B', 'E', 'A', 'D', 'G', 'B', 'E'];
export const DEFAULT_9_STRING_TUNING = ['C#', 'F#', 'B', 'E', 'A', 'D', 'G', 'B', 'E'];

// Active tuning, read from the user mirror at the moment of usage. Never captured
// at import: the mirror is hydrated after this module is evaluated, so a captured
// value would hold the fallback EADGBE and silently lose a custom tuning. Keeps the
// length guard (4-9 strings) since getSetting does no validation.
export const getGuitarTuning = () => {
  const stored = getSetting('guitarTuning', null);
  if (Array.isArray(stored) && stored.length >= 4 && stored.length <= 9) {
    return stored;
  }
  return DEFAULT_6_STRING_TUNING;
};

// Backwards-compatible name used by Settings.js to seed its tuning editor.
export const loadCustomTuning = () => getGuitarTuning();

// Persist a tuning to the user mirror. The mirror notifies its subscribers
// (useGuitarTuning), so the fretboard follows without a reload.
export const saveGuitarTuning = (tuning) => setSetting('guitarTuning', tuning);

// Reference intervals for comparing modes
export const IONIAN_INTERVALS = getModeIntervals('Ionian');
export const AEOLIAN_INTERVALS = getModeIntervals('Aeolian');

// Generate notes for a guitar string starting from a specific tuning
export const generateStringNotes = (openNote, numFrets) => {
  const allNotes = ALL_NOTES;
  const openNoteIndex = allNotes.indexOf(openNote);
  const notes = [];
  
  for (let i = 0; i <= numFrets; i++) {
    const noteIndex = (openNoteIndex + i) % 12;
    notes.push(allNotes[noteIndex]);
  }
  
  return notes;
};

// Get the note at a specific fret on a specific string
export const getNoteAtFret = (openNote, fret) => {
  const allNotes = ALL_NOTES;
  const openNoteIndex = allNotes.indexOf(openNote);
  const noteIndex = (openNoteIndex + fret) % 12;
  return allNotes[noteIndex];
};

// Absolute MIDI numbers of the open strings for the active tuning.
// The tuning only stores pitch classes, so octaves are assigned by anchoring the
// lowest string in the guitar low register (MIDI 36-47) and making each higher
// string the lowest pitch of its class at or above the previous string. This
// reproduces standard EADGBE (E2 A2 D3 G3 B3 E4) and generalises to custom tunings.
const computeOpenStringMidis = (tuning) => {
  const pcs = tuning.map(n => ALL_NOTES.indexOf(n));
  const midis = [];
  let prev = -Infinity;
  pcs.forEach((pc, i) => {
    if (i === 0) {
      midis.push(pc + 36); // octave-2 band: C2 = 36 ... B2 = 47
    } else {
      let midi = pc + 36;
      while (midi < prev) midi += 12;
      midis.push(midi);
    }
    prev = midis[i];
  });
  return midis;
};

// Computed lazily from the active tuning at usage time (not captured at import).
const getOpenStringMidis = () => computeOpenStringMidis(getGuitarTuning());

// Absolute MIDI number for a string/fret, or null if the string is out of range.
export const getFretMidi = (stringIndex, fret) => {
  const openMidi = getOpenStringMidis()[stringIndex];
  return openMidi === undefined ? null : openMidi + fret;
};

// Pitch-accurate note name with octave for a string/fret, e.g. "C#4", for piano playback.
export const getFretNoteName = (stringIndex, fret) => {
  const midi = getFretMidi(stringIndex, fret);
  if (midi === null) return null;
  const name = ALL_NOTES[midi % 12];
  const octave = Math.floor(midi / 12) - 1;
  return `${name}${octave}`;
};

// Get notes in a specific mode
export const getNotesInMode = (root, mode) => {
  return getNotesInModeFromMusicTheory(root, mode);
};

// Determine if a note is a "mode color note" - uses the colorDegrees defined for each mode
export const isModeColorNote = (note, activeScale) => {
  if (!activeScale || !activeScale.notes) return false;
  
  // Get the mode definition from MODES
  const mode = MODES[activeScale.modeName];
  if (!mode || !mode.colorDegrees) return false;
  
  // If no color degrees defined for this mode, return false
  if (mode.colorDegrees.length === 0) return false;
  
  // Find which scale degree this note is (1-based index)
  const noteIndex = activeScale.notes.indexOf(note);
  if (noteIndex === -1) return false;
  
  // Convert to 1-based scale degree (1, 2, 3, etc.)
  const scaleDegree = noteIndex + 1;
  
  // Check if this scale degree is in the colorDegrees array
  return mode.colorDegrees.includes(scaleDegree);
};

// Get the reference note (unaltered note from Ionian/Aeolian) for a mode color note
export const getReferenceNoteForModeColorNote = (note, activeScale) => {
  if (!activeScale) return null;
  
  const noteIndex = ALL_NOTES.indexOf(note);
  const rootIndex = ALL_NOTES.indexOf(activeScale.scaleRoot);
  
  // Get the current mode's intervals
  const currentModeIntervals = getModeIntervals(activeScale.modeName);
  if (!currentModeIntervals) return null;
  
  // Determine if the current mode is major-based or minor-based
  const isMajorBased = ['Ionian', 'Lydian', 'Mixolydian'].includes(activeScale.modeName) || 
                       activeScale.modeName.includes('Major') || 
                       activeScale.modeName.includes('Lydian') || 
                       activeScale.modeName.includes('Mixolydian');
  
  const isMinorBased = ['Aeolian', 'Dorian', 'Phrygian', 'Locrian'].includes(activeScale.modeName) || 
                       activeScale.modeName.includes('Minor') || 
                       activeScale.modeName.includes('Dorian') || 
                       activeScale.modeName.includes('Phrygian') || 
                       activeScale.modeName.includes('Locrian');
  
  // Get the reference intervals (Ionian for major, Aeolian for minor)
  const referenceIntervals = isMajorBased ? IONIAN_INTERVALS : 
                            isMinorBased ? AEOLIAN_INTERVALS : 
                            IONIAN_INTERVALS; // Default to Ionian if unsure
  
  // Find the interval of this note from the root
  const noteInterval = (noteIndex - rootIndex + 12) % 12;
  
  // Get the position of this interval in the current mode
  const intervalPosition = currentModeIntervals.indexOf(noteInterval);
  
  // Get the corresponding interval in the reference mode
  const referenceInterval = referenceIntervals[intervalPosition];
  
  // Calculate the reference note
  const referenceNoteIndex = (rootIndex + referenceInterval) % 12;
  return ALL_NOTES[referenceNoteIndex];
};

// Find the fret position of a specific note on a string
export const findFretForNote = (stringIndex, note, startFret, numFretsToShow) => {
  const openNote = getGuitarTuning()[stringIndex];
  const openNoteIndex = ALL_NOTES.indexOf(openNote);
  
  // Find the closest fret position for this note
  // We'll look within the visible range first
  for (let fret = startFret; fret <= startFret + numFretsToShow; fret++) {
    const fretNoteIndex = (openNoteIndex + fret) % 12;
    if (ALL_NOTES[fretNoteIndex] === note) {
      return fret;
    }
  }
  
  // If not found in visible range, look in the entire neck
  for (let fret = 0; fret < 24; fret++) {
    const fretNoteIndex = (openNoteIndex + fret) % 12;
    if (ALL_NOTES[fretNoteIndex] === note) {
      return fret;
    }
  }
  
  return -1; // Note not found on this string
};

// Check if a note is in the scale
export const isNoteInScale = (stringIndex, fret, activeScale) => {
  if (!activeScale) return false;
  
  const openNote = getGuitarTuning()[stringIndex];
  const allNotes = ALL_NOTES;
  const openNoteIndex = allNotes.indexOf(openNote);
  const noteIndex = (openNoteIndex + fret) % 12;
  const note = allNotes[noteIndex];
  
  return activeScale.notes.includes(note);
};

// Check if a note is the root note (the actual scale root, not necessarily the overall key root)
export const isRootNote = (stringIndex, fret, activeScale) => {
  if (!activeScale) return false;
  
  const openNote = getGuitarTuning()[stringIndex];
  const note = getNoteAtFret(openNote, fret);
  return note === activeScale.scaleRoot;
};

// Check if a note is a mode color note
export const isModeColor = (stringIndex, fret, activeScale) => {
  if (!activeScale) return false;
  
  const openNote = getGuitarTuning()[stringIndex];
  const note = getNoteAtFret(openNote, fret);
  return isModeColorNote(note, activeScale);
};

// Check if a note is in a specific arpeggio
export const isArpeggioNote = (stringIndex, fret, activeScale, selectedArpeggio) => {
  if (!activeScale) return false;
  
  // If no arpeggio is selected, don't highlight any notes
  if (!selectedArpeggio) {
    return false;
  }
  
  const openNote = getGuitarTuning()[stringIndex];
  const note = getNoteAtFret(openNote, fret);
  
  // Normalize note name to handle potential enharmonic differences
  const normalizedNote = note.replace(/[♯#]/g, '#').replace(/[♭b]/g, 'b');

  // First try using the notesWithDegrees property (new structure)
  if (selectedArpeggio.notesWithDegrees && Array.isArray(selectedArpeggio.notesWithDegrees)) {

    // Check if the note is in the arpeggio
    const isInArpeggio = selectedArpeggio.notesWithDegrees.some(item => {
      const normalizedArpeggioNote = item.note.replace(/[♯#]/g, '#').replace(/[♭b]/g, 'b');
      return normalizedArpeggioNote === normalizedNote;
    });

    return isInArpeggio;
  }
  
  // Fallback to using the notes property (old structure) if notesWithDegrees is not available
  // Make sure selectedArpeggio.notes exists and is an array
  if (!selectedArpeggio.notes || !Array.isArray(selectedArpeggio.notes)) {
    return false;
  }

  // Check if the note is in the arpeggio using the old structure
  const isInArpeggio = selectedArpeggio.notes.some(arpeggioNote => {
    const normalizedArpeggioNote = arpeggioNote.replace(/[♯#]/g, '#').replace(/[♭b]/g, 'b');
    return normalizedArpeggioNote === normalizedNote;
  });

  return isInArpeggio;
};

// Get the position of a note within an arpeggio
export const getArpeggioDegree = (stringIndex, fret, activeScale, selectedArpeggio) => {
  if (!activeScale) return '';
  
  // If no arpeggio is selected, don't show any degree numbers
  if (!selectedArpeggio) {
    return '';
  }
  
  const openNote = getGuitarTuning()[stringIndex];
  const note = getNoteAtFret(openNote, fret);
  
  // Normalize note name to handle potential enharmonic differences
  const normalizedNote = note.replace(/[♯#]/g, '#').replace(/[♭b]/g, 'b');
  
  // First try using the notesWithDegrees property (new structure)
  if (selectedArpeggio.notesWithDegrees && Array.isArray(selectedArpeggio.notesWithDegrees)) {
    // Find the matching note and return its scale degree
    for (const item of selectedArpeggio.notesWithDegrees) {
      const normalizedArpeggioNote = item.note.replace(/[♯#]/g, '#').replace(/[♭b]/g, 'b');
      if (normalizedArpeggioNote === normalizedNote) {
        return item.scaleDegree;
      }
    }
    return ''; // Not in arpeggio
  }
  
  // Fallback to using the notes property if notesWithDegrees is not available
  // Find the position in the arpeggio
  let position = -1;
  selectedArpeggio.notes.forEach((arpeggioNote, index) => {
    const normalizedArpeggioNote = arpeggioNote.replace(/[♯#]/g, '#').replace(/[♭b]/g, 'b');
    if (normalizedArpeggioNote === normalizedNote) {
      position = index;
    }
  });
  
  // Return the position + 1 (1-based indexing) for backwards compatibility
  return position >= 0 ? (position + 1).toString() : '';
};

// Generate the appropriate mode/scale based on the active chord note
export const getScaleForActiveChord = (rootNote, currentMode, activeChordNote) => {

  // First, ensure we can get notes for the current mode
  let notesInMode;
  try {
    notesInMode = getNotesInMode(rootNote, currentMode);
  } catch (error) {
    notesInMode = getNotesInMode(rootNote, 'Ionian');
    currentMode = 'Ionian'; // Update the current mode to match our fallback
  }

  // Locate by pitch class: the scale may be spelled with flats while
  // activeChordNote/rootNote carry the sharp key spelling.
  const activeChordPitch = resolveAllNotesIndex(activeChordNote);
  const activeChordIndex = notesInMode.findIndex(n => resolveAllNotesIndex(n) === activeChordPitch);

  if (!activeChordNote || activeChordIndex === -1 || activeChordPitch === resolveAllNotesIndex(rootNote)) {
    // If no active chord or active chord is the root, use the original mode
    return {
      notes: notesInMode,
      rootIndex: 0,
      modeName: currentMode,
      scaleRoot: rootNote
    };
  }
  
  // Extract the scale type from the currentMode
  let scaleType = 'Major'; // Default
  
  // Determine the scale type based on the current mode
  for (const type of Object.keys(MODE_NAMES_BY_SCALE)) {
    if (MODE_NAMES_BY_SCALE[type].includes(currentMode)) {
      scaleType = type;
      break;
    }
  }
  
  // For non-Western scales with only one mode or 5-note scales
  // Just return the same scale but starting from the active chord note
  const currentModeIntervals = getModeIntervals(currentMode);
  if (MODE_NAMES_BY_SCALE[scaleType].length === 1 ||
      scaleType === 'Symmetrical' ||
      (currentModeIntervals && currentModeIntervals.length === 5) ||
      (currentModeIntervals && currentModeIntervals.length === 6)) {

    // Special handling for pentatonic/non-Western scales - keep the same scale notes
    // but change the starting note for pattern generation
    return {
      notes: notesInMode, // Keep the original scale notes (e.g., A, C, D, E, G)
      rootIndex: 0,
      modeName: currentMode,
      scaleRoot: activeChordNote, // But start patterns from the chord note
      chordTones: getChordNotes(activeChordNote, 'maj') // Default to major chord for now
    };
  }
  
  // For traditional Western scales with multiple modes
  // Calculate what mode we should show based on the active chord and scale type
  const modeNames = MODE_NAMES_BY_SCALE[scaleType];

  // We need to rotate the modes array so that the currentMode is considered degree I.
  const currentModeIndex = modeNames.indexOf(currentMode);
  const rotatedIndex = (currentModeIndex + activeChordIndex) % modeNames.length;
  const relativeModeName = modeNames[rotatedIndex] || modeNames[0];

  // Safety check: use a mode we know exists
  let finalModeName = getModeIntervals(relativeModeName) ? relativeModeName : currentMode;
  if (!getModeIntervals(finalModeName)) {
    finalModeName = 'Ionian'; // Final fallback to major scale
  }

  // Get the notes of the relative mode starting from the active chord note
  let relativeScaleNotes;
  try {
    relativeScaleNotes = getNotesInMode(activeChordNote, finalModeName);
  } catch (error) {
    relativeScaleNotes = getNotesInMode(activeChordNote, 'Ionian');
    finalModeName = 'Ionian';
  }

  // La rotation relative n'est qu'une presentation : elle doit rendre le meme
  // ensemble de notes que la gamme d'origine (A Ionian vu depuis E = E
  // Mixolydian, memes notes). Un groupe non rotationnel la rend illegitime :
  // Bayati partage son scale_type avec deux pentatoniques qui ne sont pas ses
  // rotations, et le manche changeait de notes en cours de progression. Si
  // l'ensemble differe, on garde la gamme d'origine et seul le depart du motif
  // suit l'accord, comme pour les pentatoniques.
  const samePitchClasses = relativeScaleNotes.length === notesInMode.length &&
    relativeScaleNotes.every(
      (n) => notesInMode.some((m) => resolveAllNotesIndex(m) === resolveAllNotesIndex(n))
    );
  if (!samePitchClasses) {
    return {
      notes: notesInMode,
      rootIndex: 0,
      modeName: currentMode,
      scaleRoot: activeChordNote
    };
  }

  const result = {
    notes: relativeScaleNotes,
    rootIndex: 0,
    modeName: finalModeName,
    scaleRoot: activeChordNote
  };

  return result;
};

// Calculate the starting fret position based on the active chord note
export const calculateStartFret = (activeScale) => {
  // Check if activeScale is null
  if (!activeScale) {
    return 1; // Default to fret 1 if activeScale is null
  }
  
  // Find the position of the active chord note on the low E string
  const displayRoot = activeScale.scaleRoot;
  const lowEString = generateStringNotes('E', 24);
  const rootIndex = lowEString.indexOf(displayRoot);
  
  if (rootIndex >= 0) {
    // Calculate starting position (2 frets before root)
    let startFret = rootIndex - 2;
    
    // If starting position would be fret 0 or negative, use fret 1 instead
    if (startFret < 1) {
      startFret = 1;
    }
    
    return startFret;
  }
  
  // Default to fret 1 if root note not found
  return 1;
};
